import { AfterViewInit, Component, effect, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { MessagingSocketService } from '@app/finxone-web-frontend/app/lib/services/messaging-socket-service/messaging-socket-service.service';
import { NavigationService } from '@app/finxone-web-frontend/app/lib/services/navigation-service/navigation.service';
import { SupportChatsService } from '@app/finxone-web-frontend/app/lib/services/support-chats-service/support-chats.service';
import { scrollLoading } from '@app/finxone-web-frontend/app/lib/utils/scrolling-utils/scrolling.utils';
import { filterIfHandlebarNotRendered } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import {
  isValidCSS,
  transformCSSWithDynamicSelector,
} from '@app/finxone-web-frontend/app/lib/utils/themeing.utils';
import { NotificationsFilters } from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  AppBuilderPreviewState,
  baseCssConfigStyle,
  BaseCssProperties,
  NavigationOptionEnum,
  ProviderClass,
  resolveSkeletonLoadingTemplate,
  SKELETON_LAYOUT_CONFIG_PATH,
  SupportConversation,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import Handlebars from 'handlebars';
import { Subject, take, takeUntil } from 'rxjs';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { BaseComponent } from '../base/base.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-chats-list-widget',
  templateUrl: './support-chats-list-widget.component.html',
})
export class SupportChatsListWidgetComponent
  extends BaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @AutoDestroy private readonly destroy$ = new Subject<void>();
  public content = '';
  public pageNumber = 1;
  public noRecordsTemplate = '';
  public fetchingTemplate = '';
  private noRecordsTemplateFunction: HandlebarsTemplateDelegate = () => '';
  private fetchingTemplateFunction: HandlebarsTemplateDelegate = () => '';

  public selectedOption: { label: string; value: string };
  public filterOptions: { name: string; value: NotificationsFilters; isActive: boolean }[] = [];
  public filtersEnabled = false;
  public filterMarginStyle: baseCssConfigStyle;

  public isLoading: boolean;
  public appBuilderShowFetchingContent = false;
  public appBuilderShowNoRecordsContent = false;

  public chatsList: Array<SupportConversation>;
  public hasCustomStyles: boolean;
  public isGlobalStylingEnabled = false;
  public containerHeight: string;
  public activeChatId: string | null = null;
  public activeFilter = NotificationsFilters.All;
  private totalItems: number | undefined;

  constructor(
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private readonly configService: ConfigService,
    private readonly router: Router,
    public pageConfig: PageConfigurationProvider,
    private readonly el: ElementRef,
    private readonly supportChatsService: SupportChatsService,
    private readonly navigationService: NavigationService,
    private readonly messagingSocketService: MessagingSocketService,
  ) {
    super();
    effect(() => {
      this.activeChatId = this.supportChatsService.activeChatId();
      // Fetch chats when active chat ID changes, this is for the case when the list and chat widgets are in the same page and user creates a new conversation
      if (this.activeChatId) this.fetchSupportConversations();
    });
  }

  ngOnInit(): void {
    if (this.dataIn) {
      this.attri = this.dataIn.attri ?? null;
      this.onLoadWidgetConfiguration();
      this.initHandleBars();
      this.setAppBuilderPreviewState();
      this.applyCustomStyles();
    }
    this.fetchSupportConversations();
    this.setupFilterPills();
    this.messagingSocketService.newMessageEvent$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.fetchSupportConversations();
    });
  }

  private fetchSupportConversations() {
    this.isLoading = true;
    this.supportChatsService
      .getSupportChats(this.pageNumber, this.pageSize, this.activeFilter)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.chatsList = response.result;
          this.pageNumber = response.page;
          this.totalItems = response.totalItems;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error fetching user chats:', error);
          this.isLoading = false;
        },
      });
  }

  ngAfterViewInit(): void {
    // Wait for DOM to be fully rendered before calculating height
    requestAnimationFrame(() => {
      this.evaluateListContainerElementHeight();
    });
  }

  private evaluateListContainerElementHeight() {
    this.containerHeight = scrollLoading('chats-list', this.el);
  }

  private onLoadWidgetConfiguration(): void {
    this.isGlobalStylingEnabled = !!this.attri?.widgetProperties?.['globalStyling'];
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BOX_SHADOW,
    ]);
    this.filterMarginStyle = this.configService.getSpecificBaseCssStyle(this.attri['filterPillConfig'], [
      BaseCssProperties.MARGIN,
    ]);
  }

  private initHandleBars() {
    this.content = this.attri?.widgetProperties?.textContent ?? '';
    this.noRecordsTemplate = this.attri?.['noRecordsTemplate'] ?? '';
    this.fetchingTemplate = this.attri?.['fetchingTemplate'] ?? '';
    this.noRecordsTemplateFunction = Handlebars.compile(this.noRecordsTemplate);
    this.fetchingTemplateFunction = Handlebars.compile(this.fetchingTemplate);
  }

  private setAppBuilderPreviewState(): void {
    if (!this.configService.getActiveConfigType().isWorkflow) {
      return;
    }
    const appBuilderState = this.attri?.widgetProperties?.['appBuilderState'];
    this.appBuilderShowFetchingContent = appBuilderState === AppBuilderPreviewState.FETCHING;
    this.appBuilderShowNoRecordsContent = appBuilderState === AppBuilderPreviewState.NO_RECORDS;
  }

  private setupFilterPills() {
    this.filtersEnabled = this.attri['filterPillConfig']?.['isEnabled'] ?? false;
    this.filterOptions =
      this.attri['filterPillConfig']?.['filterOptions']?.map((option: { name: string; value: string }) => ({
        name: option.name,
        value: option.value,
        isActive: false,
      })) ?? [];
    const allFilterOption = this.filterOptions.find((option) => option.value === 'all');
    if (this.filtersEnabled && allFilterOption) {
      allFilterOption.isActive = true;
    }
  }

  public get pageSize(): number {
    if (this.attri['pageSize'] === null) {
      return 10;
    } else {
      return this.attri['pageSize'];
    }
  }

  public get pagingEnabled(): boolean {
    if (this.attri['pagingEnabled'] === null) {
      return true;
    } else {
      return this.attri['pagingEnabled'];
    }
  }

  private handleMarkAsRead(chat: SupportConversation): void {
    this.supportChatsService
      .markSupportThreadAsRead(chat.id, chat.lastMessage.id)
      .pipe(take(1))
      .subscribe(() => {
        const chatIndex = this.chatsList.findIndex((c) => c.id === chat.id);
        if (chatIndex !== -1) {
          this.chatsList[chatIndex].unreadCount = 0;
        }
      });
  }

  private applyCustomStyles() {
    this.hasCustomStyles = isValidCSS(this.attri?.['customStyles'] ?? '');

    // Skip DOM manipulation to prevent applying invalid CSS
    if (!this.hasCustomStyles) return;

    const customStyles = this.attri?.['customStyles'] ?? '';
    const newTextContent = transformCSSWithDynamicSelector(customStyles, '#chats-list');

    // check if existing styles are the same as new one then no need to append new styles tag
    const existingStyleElement = document.getElementById('chatsListStyle');
    if (existingStyleElement?.textContent === newTextContent) {
      return;
    }

    // remove existing styles if they are different
    if (existingStyleElement) {
      existingStyleElement.remove();
    }

    // append new styles
    const styleElement = document.createElement('style');
    styleElement.id = 'chatsListStyle';
    styleElement.textContent = newTextContent;
    document.head.appendChild(styleElement);
  }

  public renderNoRecordsTemplate(): string {
    return filterIfHandlebarNotRendered(this.noRecordsTemplateFunction({}));
  }

  public renderFetchingTemplateFunction(): string {
    return resolveSkeletonLoadingTemplate(
      this.attri?.[SKELETON_LAYOUT_CONFIG_PATH],
      filterIfHandlebarNotRendered(this.fetchingTemplateFunction({})),
    );
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const scrollPercentage = (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
    if (scrollPercentage > 80 && !this.isLoading) {
      this.handleChildScroll();
    }
  }

  handleChildScroll() {
    if (this.pagingEnabled) {
      if (this.pageNumber) {
        this.pageNumber = this.pageNumber + 1;
        this.fetchSupportConversations();
      }
    }
  }

  public onFilterChange(filter: { name: string; value: string; isActive: boolean }) {
    const shouldFetchAllNotifications =
      !filter.isActive &&
      (filter.value === NotificationsFilters.Read || filter.value === NotificationsFilters.Unread);

    const effectiveValue: NotificationsFilters = shouldFetchAllNotifications
      ? NotificationsFilters.All
      : (filter.value as NotificationsFilters);

    this.filterOptions = this.filterOptions.map((f) => {
      return {
        ...f,
        isActive: f.value === effectiveValue,
      };
    });
    this.activeFilter = effectiveValue;
    this.fetchSupportConversations();
  }

  public getWidgetStyles(chat: SupportConversation): baseCssConfigStyle {
    // a linked item takes its box from the theme's List styling; only the unread highlight stays here
    if (this.isGlobalStylingEnabled) {
      return chat.unreadCount === 0
        ? { 'font-weight': 'normal' }
        : { 'font-weight': '600', 'border-color': this.attri.css_config.alertColor };
    }
    return {
      ...this.contentStyle,
      'border-color':
        chat.unreadCount === 0 ? this.attri.css_config.borderColor : this.attri.css_config.alertColor,
      'font-weight': chat.unreadCount === 0 ? 'normal' : '600',
      'border-width': this.attri.css_config.borderWidth + 'px',
      'border-style': this.attri.css_config.borderStyle,
      'border-bottom-left-radius': this.returnRadiusWithUnit(
        this.attri.css_config.borderRadiusBottomLeft ?? 0,
      ),
      'border-top-left-radius': this.returnRadiusWithUnit(this.attri.css_config.borderRadiusTopLeft ?? 0),
      'border-bottom-right-radius': this.returnRadiusWithUnit(
        this.attri.css_config.borderRadiusBottomRight ?? 0,
      ),
      'border-top-right-radius': this.returnRadiusWithUnit(this.attri.css_config.borderRadiusTopRight ?? 0),
    };
  }

  private returnRadiusWithUnit(cssValue: number): string {
    return cssValue.toString() + this.attri.css_config.borderRadiusUnit;
  }

  ngOnDestroy(): void {
    if (this.activeChatId) {
      this.messagingSocketService.leaveThread(this.activeChatId ?? '');
    }
    if (this.messagingSocketService.socket?.connected) {
      this.messagingSocketService.disconnect();
    }
  }

  public onChatClick(chat: SupportConversation): void {
    this.messagingSocketService.joinThread(chat.id);
    this.supportChatsService.setActiveChatId(chat.id);
    this.supportChatsService.setActiveChatStatus(chat.status);
    this.supportChatsService.setNewChatStatus(false); // set new chat status to false to avoid resetting messages list
    if (this.activeChatId && this.activeChatId !== chat.id) {
      this.messagingSocketService.leaveThread(this.activeChatId);
    }
    if (chat.unreadCount > 0) {
      this.handleMarkAsRead(chat);
    }
    if (
      this.attri?.['navigationOption'] &&
      this.attri?.['navigationOption'] !== NavigationOptionEnum.USE_AS_BLANK
    ) {
      const { pageUrl } = this.navigationService.buildPageNavigationUrl({
        pageId: this.attri?.['pageId'] || '',
        zone: this.attri?.['zone'] || '',
        pageUrl: this.attri?.['pageUrl'] || '',
        role: this.attri?.['role'] || '',
        navigationOption: this.attri['navigationOption'],
        tabRef: this.attri?.['tabRef'] ?? '',
        pageTransition: this.attri?.['pageTransition'] ?? 'forward',
        browserBehaviour: this.attri?.['browserBehaviour'],
        externalUrl: this.attri?.['externalUrl'],
      });
      this.router.navigateByUrl(pageUrl);
    }
  }
}
